// localStorage helpers for the auth UI.
//
// We store: the access + refresh token (so the user can hit /me, refresh
// later, etc.), the pending verify state, and the most recently seen
// nextUrl from the URL query string. nextUrl persists through the OTP
// step and the onboarding wizard so the final redirect lands the user
// where they originally tried to go.

const KEYS = {
  TOKEN: 'vivid360.auth.token',
  REFRESH: 'vivid360.auth.refreshToken',
  USER: 'vivid360.auth.user',
  NEXT: 'vivid360.auth.next',
  SOURCE: 'vivid360.auth.source',
  PENDING_VERIFY: 'vivid360.auth.pendingVerify',
  // The company name typed at sign-up, so onboarding step 1 opens with it.
  SIGNUP_COMPANY: 'vivid360.auth.signupCompany',
};

export const storage = {
  saveAuth({ token, refreshToken, user }) {
    if (token) localStorage.setItem(KEYS.TOKEN, token);
    if (refreshToken) localStorage.setItem(KEYS.REFRESH, refreshToken);
    if (user) localStorage.setItem(KEYS.USER, JSON.stringify(user));
  },
  getToken: () => localStorage.getItem(KEYS.TOKEN),
  getRefresh: () => localStorage.getItem(KEYS.REFRESH),
  getUser: () => {
    const raw = localStorage.getItem(KEYS.USER);
    try { return raw ? JSON.parse(raw) : null; } catch { return null; }
  },
  clearAuth() {
    localStorage.removeItem(KEYS.TOKEN);
    localStorage.removeItem(KEYS.REFRESH);
    localStorage.removeItem(KEYS.USER);
    localStorage.removeItem(KEYS.PENDING_VERIFY);
  },

  // ── nextUrl + signup_source come in via URL query params on first
  //    landing. Keep them in storage so they survive page reloads
  //    and the multi-step signup → verify-email → onboard flow.
  setRouting({ next, source }) {
    if (next) localStorage.setItem(KEYS.NEXT, next);
    if (source) localStorage.setItem(KEYS.SOURCE, source);
  },
  getNext: () => localStorage.getItem(KEYS.NEXT) || '',
  getSource: () => localStorage.getItem(KEYS.SOURCE) || '',
  clearRouting() {
    localStorage.removeItem(KEYS.NEXT);
    localStorage.removeItem(KEYS.SOURCE);
  },
  // Drop ONLY the source, keeping nextUrl alive for the rest of the
  // verify → onboard flow. Called once a signup has consumed the source:
  // it is now recorded on the user server-side and must not leak into the
  // next signup from this browser. Without this, a single visit carrying
  // ``?from=leadstream`` tags every later account created here, because
  // ``captureRoutingFromUrl`` falls back to the stored value and
  // ``clearRouting`` only runs at logout or at the end of onboarding —
  // which tool signups skip.
  clearSource() { localStorage.removeItem(KEYS.SOURCE); },

  setPendingVerify(payload) {
    localStorage.setItem(KEYS.PENDING_VERIFY, JSON.stringify(payload));
  },
  getPendingVerify() {
    const raw = localStorage.getItem(KEYS.PENDING_VERIFY);
    try { return raw ? JSON.parse(raw) : null; } catch { return null; }
  },
  clearPendingVerify() { localStorage.removeItem(KEYS.PENDING_VERIFY); },
  setSignupCompany(name) {
    try { if (name) localStorage.setItem(KEYS.SIGNUP_COMPANY, name); } catch { /* ignore */ }
  },
  getSignupCompany() {
    try { return localStorage.getItem(KEYS.SIGNUP_COMPANY) || ''; } catch { return ''; }
  },
  clearSignupCompany() {
    try { localStorage.removeItem(KEYS.SIGNUP_COMPANY); } catch { /* ignore */ }
  },
};

// ?reason= and ?email= on /login (AUTH-507): why the user is back and who
// they were. Read from the given query string, never stored, and never
// rendered as text: ``reason`` only selects a key of a fixed map.
export function readNoticeFromSearch(search) {
  const sp = new URLSearchParams(search || '');
  return { reason: sp.get('reason') || '', email: (sp.get('email') || '').trim() };
}

// Pull ?next= and ?from= off the current URL and remember them. Returns
// the (possibly updated) values so a caller can use them right away.
export function captureRoutingFromUrl() {
  const sp = new URLSearchParams(window.location.search);
  const next = sp.get('next') || '';
  const source = sp.get('from') || '';
  if (next || source) storage.setRouting({ next, source });
  return { next: next || storage.getNext(), source: source || storage.getSource() };
}
